import axios, {AxiosError, AxiosInstance, AxiosResponse} from 'axios';

import {localeDate} from './localeDate';

import * as Sentry from '@sentry/react';

export const BASEURL = process.env.REACT_APP_BASEURL;

/**
 * Axios 싱글톤 클래스
 * @public
 */
export class AxiosSingleton {
  private axiosInstance!: AxiosInstance;
  private isRefreshing!: boolean;
  private readonly maxRetry = 3;
  private readonly maxQueueLength = 3;
  private retryCount!: number;
  private refreshQueue!: any[];
  private static instance: AxiosSingleton;
  private readonly commonRefreshURL = '/users/kiosk/tokens/member';
  private readonly centerRefreshURL = '/users/kiosk/tokens/admin';
  constructor() {
    if (AxiosSingleton.instance) {
      return AxiosSingleton.instance;
    }

    AxiosSingleton.instance = this;
    const options = {
      baseURL: BASEURL,
      timeout: 10000,
    };
    AxiosSingleton.instance.axiosInstance = axios.create(options);
    AxiosSingleton.instance.setupInterceptors();

    AxiosSingleton.instance.isRefreshing = false; // Track if a token refresh is already in progress
    AxiosSingleton.instance.refreshQueue = []; // Queue to store requests while token is being refreshed
    AxiosSingleton.instance.retryCount = 0;
  }

  /**
   * 재시도 카운트 리셋
   * @public
   */
  private resetRetryCount() {
    AxiosSingleton.instance.retryCount = 0;
  }

  /**
   * 토큰 리프레시 실행
   * @param error - Axios 응답 에러
   * @returns Promise
   * @public
   */
  async executeRefresh(error: AxiosResponse): Promise<any> {
    AxiosSingleton.instance.isRefreshing = true;
    AxiosSingleton.instance.retryCount += 1;
    let refreshResult: AxiosResponse<any> | AxiosError | any;
    refreshResult = await AxiosSingleton.instance.refreshAccessToken();
    if (AxiosSingleton.instance.retryCount < AxiosSingleton.instance.maxRetry) {
      if (refreshResult.status && refreshResult.status === 201) {
        let authorization: any;
        let centerAuthorization: any;
        if (
          AxiosSingleton.instance.axiosInstance.defaults.headers.common
            .authorization
        ) {
          authorization =
            AxiosSingleton.instance.axiosInstance.defaults.headers.common
              .authorization;
          error.config.headers.authorization = authorization;
        }
        if (
          AxiosSingleton.instance.axiosInstance.defaults.headers.common[
            'authorization-center'
          ]
        ) {
          centerAuthorization =
            AxiosSingleton.instance.axiosInstance.defaults.headers.common[
              'authorization-center'
            ];
          error.config.headers['authorization-center'] = centerAuthorization;
        }
        await Promise.all(
          AxiosSingleton.instance.refreshQueue.map(queuedRequest => {
            queuedRequest.config.headers.authorization = authorization;
            queuedRequest.config.headers['authorization-center'] =
              centerAuthorization;

            return AxiosSingleton.instance.axiosInstance.request(
              queuedRequest.config,
            );
          }),
        );

        AxiosSingleton.instance.isRefreshing = false;
        AxiosSingleton.instance.resetRetryCount();
        AxiosSingleton.instance.refreshQueue = [];
        return AxiosSingleton.instance.axiosInstance.request(error.config);
      } else {
        return await AxiosSingleton.instance.executeRefresh(error /* , true */);
      }
    }
    AxiosSingleton.instance.isRefreshing = false;
    AxiosSingleton.instance.resetRetryCount();
    const myError: any = new Error('Refresh Failed Exception');
    myError.name = 'RefreshFailedException';
    myError.response = {
      message: 'Exception',
      status: 'GoHome',
      type: 'CENTER',
      state: 'modal',
      detail: 'tokenExceptionDetail',
      title: 'tokenExceptionTitle',
      errorCode: 'errorCode',
    };
    return Promise.reject(myError);
  }

  /**
   * 응답 성공 핸들러
   * @param response - Axios 응답
   * @returns 응답 객체
   * @public
   */
  handleFulfill(response: AxiosResponse) {
    if (response.data?.accessToken && response.data?.refreshToken) {
      const userInfoLocalStorage = JSON.parse(
        localStorage.getItem('userInfo') || 'null',
      );
      const userInfoSessionStorage = JSON.parse(
        sessionStorage.getItem('userInfo') || 'null',
      );
      if (userInfoLocalStorage !== null) {
        userInfoLocalStorage.accessToken = response.data.accessToken;
        userInfoLocalStorage.refreshToken = response.data.refreshToken;
        localStorage.setItem('userInfo', JSON.stringify(userInfoLocalStorage));
      } else if (userInfoSessionStorage !== null) {
        userInfoSessionStorage.accessToken = response.data.accessToken;
        userInfoSessionStorage.refreshToken = response.data.refreshToken;
        sessionStorage.setItem(
          'userInfo',
          JSON.stringify(userInfoSessionStorage),
        );
      }
    }

    return response;
  }

  /**
   * 응답 실패 핸들러
   * @param error - Axios 에러
   * @returns Promise
   * @public
   */
  async handleReject(error: AxiosError | Error | any) {
    Sentry.captureMessage(error);
    if (error.response) {
      if (error.response.status === 401) {
        if (!AxiosSingleton.instance.isRefreshing) {
          return await AxiosSingleton.instance.executeRefresh(
            error /* , isCenter */,
          );
        } else {
          /**
           * 토큰 리프레시 요청이 아닌 것만 refreshQueue 에 담는다.
           */
          if (
            !error.config.url.includes(
              AxiosSingleton.instance.commonRefreshURL,
            ) &&
            !error.config.url.includes(AxiosSingleton.instance.centerRefreshURL)
          ) {
            if (
              AxiosSingleton.instance.refreshQueue.length <
              AxiosSingleton.instance.maxQueueLength
            ) {
              AxiosSingleton.instance.refreshQueue.push({
                config: error.config,
              });
            }
            return error;
          }
          return error;
        }
      } else if (error.response.status === 400) {
        if (error.response.data.message.includes('Invalid authorization')) {
          const myError: any = new Error('Expired certification');
          myError.name = 'Expired certification';
          myError.response = {
            message: 'Expired certification',
            status: 'GoHome',
            type: 'CENTER',
            state: 'modal',
            title: 'modalErrorSmsReTryTitle',
            detail: 'modalErrorSmsReTryDetail',
            errorCode: error.response.status,
            serverMessage: error.response.data.message,
          };
          return Promise.reject(myError);
        }

        if (error.response.data.type === 'NEED_SIGN_OUT') {
          const myError: any = new Error('BadRequestException NEED_SIGN_OUT');
          myError.name = 'BadRequestException NEED_SIGN_OUT';
          myError.response = {
            message: 'BadRequestException NEED_SIGN_OUT',
            status: 'GoHome',
            type:
              error.response.data.type === 'MEMBER'
                ? 'MEMBER'
                : error.response.data.type === 'CENTER'
                ? 'CENTER'
                : 'NULL',
            state: 'modal',
            title: 'modalErrorNotFoundTitle',
            detail: 'modalErrorNotFoundDetail',
            errorCode: error.response.status,
          };
          return Promise.reject(myError);
        } else {
          const myError: any = new Error('BadRequestException error1');
          myError.name = 'BadRequestException error1';
          myError.response = {
            message: 'BadRequestException error1',
            type:
              error.response.data.type === 'MEMBER'
                ? 'MEMBER'
                : error.response.data.type === 'CENTER'
                ? 'CENTER'
                : 'NULL',
            state: 'modal',
            title: 'modalErrorNotFoundTitle',
            detail: 'modalErrorNotFoundDetail',
            errorCode: error.response.status,
            serverMessage: error.response.data.message,
          };
          return Promise.reject(myError);
        }
      } else if (error.response.status === 403) {
        const myError: any = new Error('ForbiddenException error');
        myError.name = 'ForbiddenException error';
        myError.response = {
          message: 'ForbiddenException error',
          status: 'GoHome',
          type:
            error.response.data.type === 'MEMBER'
              ? 'MEMBER'
              : error.response.data.type === 'CENTER'
              ? 'CENTER'
              : 'NULL',
          state: 'modal',
          title: 'modalErrorForbiddenTitle',
          detail: 'modalErrorForbiddenDetail',
          errorCode: error.response.status,
        };
        return Promise.reject(myError);
      } else if (error.response.status === 500) {
        const myError: any = new Error('Internal server error');
        if (
          error.response.data.message &&
          error.response.data.message.includes('AI API')
        ) {
          myError.name = 'AI API Internal server error';
          myError.response = {
            message: 'AI API Internal server error',
            status: 'AI API Internal server error',
            type: 'CENTER',
            state: 'modal',
            title: 'reshootPhoto',
            detail: 'findPhotoError',
            errorCode: error.response.status,
          };
        } else {
          myError.name = 'Internal server error';
          myError.response = {
            message: 'Internal server error',
            status: 'Internal server error',
            type: 'CENTER',
            state: 'modal',
            title: 'modalErrorServerTitle',
            detail: 'modalErrorServerDetail',
            errorCode: error.response.status,
          };
        }

        return Promise.reject(myError);
      } else if (error.response.status === 429) {
        if (!error.response.data.type) {
          const myError: any = new Error('OverDevices');
          myError.name = 'OverDevices';
          myError.response = {
            message: 'OverDevices',
            status: 'OverDevices',
            type: 'CENTER',
            state: 'modal',
            title: 'modalErrorOverTitle',
            detail: 'modalErroOverrDetail',
            errorCode: error.response.status,
          };
          return Promise.reject(myError);
        } else {
          const myError: any = new Error('TooManyRequestException429');
          myError.name = 'TooManyRequestException429';
          myError.response = {
            message: 'TooManyRequestException429',
            status: 'TooMany',
            type: 'CENTER',
            state: 'modal',
            detail: 'modalErrorDetail',
            title: 'modalErrorTitle',
            errorCode: error.response.status,
          };
          return Promise.reject(myError);
        }
      } else if (error.response.status === 410) {
        const myError: any = new Error('Expired certification');
        myError.name = 'Expired certification';
        myError.response = {
          message: 'Expired certification',
          status: 'Expired',
          type: 'CENTER',
          state: 'modal',
          title: 'modalErrorSmsReTryTitle',
          detail: 'modalErrorSmsReTryDetail',
          errorCode: error.response.status,
        };
        return Promise.reject(myError);
      } else if (error.response.status === 409) {
        const myError: any = new Error('Conflict');
        myError.name = 'Conflict';
        myError.response = {
          message: 'Conflict',
          status: 'Conflict',
          type: 'CENTER',
          state: 'modal',
          title: 'modalErrorConflictTitle',
          detail: 'modalErrorConflictDetail',
          errorCode: error.response.status,
        };
        return Promise.reject(myError);
      } else if (error.response.status === 408) {
        const myError: any = new Error('TimeOut');
        myError.name = 'TimeOut';
        myError.response = {
          message: 'TimeOut',
          status: 'TimeOut',
          type: 'CENTER',
          state: 'modal',
          title: 'modalErrorTitle',
          detail: 'modalErroGoHomeDetail',
          errorCode: error.response.status,
        };
        return Promise.reject(myError);
      } else if (error.response.status === 503) {
        let originError = JSON.parse(error.response.data.message);
        const myError: any = new Error('Expired certification');
        myError.name = 'Expired certification';
        myError.response = {
          message: 'Expired certification',
          status: 'GoOut',
          type: 'CENTER',
          state: 'modal',
          title: '서버 점검중입니다.',
          detail: originError.status.description,
          startTime: localeDate(originError.status.issuedAt),
          endTime: localeDate(originError.status.estimatedCompletionTime),
          childStatusHistoryList: originError.status.childStatusHistoryList,
          errorCode: error.response.status,
        };
        return Promise.reject(myError);
      } else {
        const myError: any = new Error('Exception');
        myError.name = 'Exception';
        myError.response = {
          message: 'Exception',
          status: 'TooMany',
          type: 'CENTER',
          state: 'modal',
          detail: 'modalErrorDetail',
          title: 'modalErrorTitle',
          errorCode: error.response.status,
        };
        return Promise.reject(myError);
      }
    } else {
      const myError: any = new Error('Exception');
      myError.name = 'Exception';
      myError.response = {
        message: 'Exception',
        status: 'TooMany',
        type: 'CENTER',
        state: 'modal',
        detail: 'exceptionDetail',
        title: 'exceptionTitle',
        errorCode: 'errorCode',
      };

      return Promise.reject(myError);
    }
  }

  /**
   * 인터셉터 설정
   * @public
   */
  setupInterceptors() {
    AxiosSingleton.instance.axiosInstance.interceptors.request.use(
      config => {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
        if (userInfo?.accessToken) {
          config.headers.authorization = userInfo.accessToken;
        }
        return config;
      },
      error => Promise.reject(error),
    );

    AxiosSingleton.instance.axiosInstance.interceptors.response.use(
      AxiosSingleton.instance.handleFulfill,
      AxiosSingleton.instance.handleReject,
    );
  }

  /**
   * 액세스 토큰 리프레시
   * @returns Promise
   * @public
   */
  async refreshAccessToken() {
    // Token refresh logic
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');

    try {
      const result: any = await AxiosSingleton.instance.axiosInstance.post(
        AxiosSingleton.instance.commonRefreshURL,
        {
          accessToken: userInfo?.accessToken,
          refreshToken: userInfo?.refreshToken,
        },
      );
      if (result.response) {
        if (result.response.status === 201) {
          const newAccessToken = result.response.data.accessToken;
          AxiosSingleton.instance.axiosInstance.defaults.headers.common.authorization =
            newAccessToken;
          if (userInfo !== null) {
            userInfo.accessToken = result.response.data.accessToken;
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
          }
          AxiosSingleton.instance.isRefreshing = false;
          return result.response;
        }
        return result;
      } else {
        if (result.status === 201) {
          const newAccessToken = result.data.accessToken;
          AxiosSingleton.instance.axiosInstance.defaults.headers.common.authorization =
            newAccessToken;
          if (userInfo !== null) {
            userInfo.accessToken = result.data.accessToken;
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
          }

          AxiosSingleton.instance.isRefreshing = false;
          return result;
        }
        return result;
      }
    } catch (error) {
      return error;
    }
  }

  /**
   * Axios 인스턴스 가져오기
   * @returns Axios 인스턴스
   * @public
   */
  getAxiosInstance() {
    return AxiosSingleton.instance.axiosInstance;
  }

  /**
   * 싱글톤 인스턴스 가져오기
   * @returns 싱글톤 인스턴스
   * @public
   */
  getInstance() {
    return AxiosSingleton.instance;
  }
}
export const baseAPI = new AxiosSingleton();
